import {
  createBaseThemeOptions,
  createUnifiedTheme,
  palettes,
  UnifiedThemeOptions,
} from '@backstage/theme';
import { blue, gray, green, purple, red, white, yellow } from '@carbon/colors';
import { body01, fontFamilies } from '@carbon/type';

/**
 * IBM Light Theme - Clean Implementation
 * Uses theme tokens and Backstage's theme system properly
 */
export const ibmLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      mode: 'light',

      primary: {
        main: blue[60],
        light: blue[50],
        dark: blue[70],
        contrastText: white,
      },
      secondary: {
        main: purple[70],
        light: purple[50],
        dark: purple[80],
        contrastText: white,
      },
      error: {
        main: red[60],
        light: red[50],
        dark: red[60],
      },
      warning: {
        main: yellow[30],
        light: yellow[10],
        dark: yellow[30],
      },
      info: {
        main: blue[60],
        light: blue[50],
        dark: blue[70],
      },
      success: {
        main: green[60],
        light: green[50],
        dark: green[60],
      },
      background: {
        default: white,
        paper: white,
      },
      text: {
        primary: gray[100],
        secondary: gray[70],
        disabled: gray[50],
      },
      divider: gray[20],
      navigation: {
        background: gray[10],
        indicator: blue[60],
        color: gray[70],
        selectedColor: gray[100],
        navItem: {
          hoverBackground: gray[20],
        },
      },
      // Required by @backstage-community/plugin-cost-insights, which extends
      // the palette with a `tooltip` key. Without this the plugin falls back
      // to undefined and renders an unreadable tooltip.
      tooltip: {
        background: gray[100],
        color: gray[10],
      },
    } as any,
  }),

  defaultPageTheme: 'home',
  fontFamily: fontFamilies.sans,

  // Use theme shape instead of CSS
  shape: {
    borderRadius: 0, // Carbon sharp corners
  },

  // Default props for components
  defaultComponentProps: {
    MuiTypography: {
      color: 'textPrimary',
    },
  },

  components: {
    // Global CSS baseline
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: gray[100],
        },
        main: {
          color: gray[100],
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }: any) => ({
          backgroundImage: 'unset',
          backgroundColor: theme.palette.navigation.background,
          color: theme.palette.text.primary,
          borderBottom: `4px solid ${theme.palette.primary.main}`,
          // Ensure all header content is visible
          '& *': {
            color: 'inherit',
          },
          '& svg': {
            color: theme.palette.text.primary,
            fill: 'currentColor',
          },
          '& button': {
            color: theme.palette.text.primary,
          },
          '& a': {
            color: theme.palette.text.primary,
          },
          // Icon buttons specifically
          '& .MuiIconButton-root': {
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor:
                theme.palette.navigation.navItem?.hoverBackground,
            },
          },
          // Subtitles and headings in header
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: theme.palette.text.primary,
          },
          // Typography variants
          '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
            color: theme.palette.text.secondary,
          },
        }),
        title: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
        subtitle: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        // Upstream `type` (entity-kind label, e.g. "component — github:github-repo")
        // and `breadcrumb` (TechDocs path) both color from `theme.page.fontColor`,
        // which defaults to white for the legacy dark Backstage header. Our header
        // is light, so override to a readable on-light color.
        type: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        breadcrumb: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },

    BackstageSidebar: {
      styleOverrides: {
        drawer: ({ theme }: any) => ({
          backgroundColor: theme.palette.navigation.background,
          borderRight: `1px solid ${theme.palette.divider}`,
        }),
      },
    },

    // Backstage InfoCard and EntityLayout components
    BackstageInfoCard: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          '& *': {
            color: 'inherit',
          },
        }),
      },
    },

    BackstageHeaderLabel: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        label: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
        }),
        value: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
        elevation1: {
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: fontFamilies.sans,
          fontWeight: 400,
          letterSpacing: body01.letterSpacing,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: blue[70],
          },
          '&:active': {
            backgroundColor: blue[80],
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: purple[80],
          },
        },
        outlined: ({ theme }: any) => ({
          borderColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}1a`, // 10% opacity
            borderColor: theme.palette.primary.dark,
          },
        }),
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontFamily: fontFamilies.sans,
        }),
        head: {
          backgroundColor: gray[20],
          fontWeight: 600,
          letterSpacing: body01.letterSpacing,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16, // Exception to zero border-radius
          fontFamily: fontFamilies.sans,
        },
        filled: {
          backgroundColor: gray[20],
        },
        outlined: {
          borderColor: gray[50],
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          textTransform: 'none',
          fontFamily: fontFamilies.sans,
          letterSpacing: body01.letterSpacing,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }: any) => ({
          backgroundColor: theme.palette.primary.main,
          height: 3,
        }),
      },
    },

    // Input components
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
        }),
      },
    },

    // Icon buttons - ensure visibility
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor:
              theme.palette.navigation.navItem?.hoverBackground || gray[20],
          },
        }),
      },
    },

    // SVG Icons - ensure visibility
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          color: theme.palette.text.primary,
        }),
      },
    },

    // Typography - ensure all text is readable with proper defaults
    MuiTypography: {
      defaultProps: {
        color: 'textPrimary',
      },
      styleOverrides: {
        root: {
          // Let color inherit from variant or parent
          // This avoids hardcoding colors everywhere

          // Inside the cost-insights chart tooltip (background is dark in
          // light mode), text must inherit from the tooltip container so it
          // stays readable. The `makeStyles-tooltip-*` class comes from
          // @backstage-community/plugin-cost-insights useTooltipStyles().
          '[class*="makeStyles-tooltip-"] &': {
            color: 'inherit',
          },
        },
        h1: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h2: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h3: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h4: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h5: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        h6: ({ theme }: any) => ({
          color: theme.palette.text.primary,
          fontWeight: 600,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        body1: {
          // Inherit from root
        },
        body2: {
          // Inherit from root
        },
        subtitle1: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        subtitle2: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
        caption: ({ theme }: any) => ({
          color: theme.palette.text.secondary,
          '[class*="makeStyles-tooltip-"] &': { color: 'inherit' },
        }),
      },
    },

    // Focus states
    MuiFocusVisible: {
      styleOverrides: {
        root: {
          outline: `2px solid ${blue[60]}`,
          outlineOffset: '2px',
        },
      },
    },
  },
} as UnifiedThemeOptions);
